import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { User, Search, CheckCircle2, AlertCircle, Camera } from "lucide-react";
import { useState, useMemo, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { FotosVeiculo, chaveFotosVeiculo, useFotosVeiculo } from "@/components/veiculos/FotosVeiculo";
import { mensagemFotosFaltantes, posicoesFaltantes } from "@shared/veiculo-fotos";
import type { UserProfile } from "@shared/schema";

interface SelecionarTecnicoVistoriaProps {
  tecnicosDisponiveis: UserProfile[];
  tecnicoAtual?: UserProfile | null;
  onSelect: (tecnico: UserProfile) => Promise<void>;
  isLoading?: boolean;
  laudoNaoVisitoriado: boolean;
  /** Flag congelada do laudo: com o módulo Detran, a primeira atribuição exige as 4 fotos do veículo. */
  exigeFotosVeiculo?: boolean;
  veiculoId?: string | null;
}

export function SelecionarTecnicoVistoria({
  tecnicosDisponiveis,
  tecnicoAtual,
  onSelect,
  isLoading = false,
  laudoNaoVisitoriado,
  exigeFotosVeiculo = false,
  veiculoId,
}: SelecionarTecnicoVistoriaProps) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(
    tecnicoAtual?.id || null,
  );
  const [isSelecting, setIsSelecting] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const [enviandoFoto, setEnviandoFoto] = useState(false);
  const [faltou, setFaltou] = useState(false);

  // A regra vale só na primeira atribuição (o banco não exige fotos para trocar o técnico).
  const exigeFotos = exigeFotosVeiculo && !tecnicoAtual;
  const fotos = useFotosVeiculo(veiculoId, open && exigeFotos);
  const faltam = fotos.isSuccess ? posicoesFaltantes(fotos.data) : [];
  // Carregando, erro de consulta e veículo ausente também bloqueiam: erro nunca vale como "completo".
  const bloqueado = exigeFotos && (!fotos.isSuccess || faltam.length > 0);
  // Quando a última foto entra, a grade e o aviso de sucesso ficam até fechar o diálogo.
  const mostrarFotos = exigeFotos && (bloqueado || faltou);

  useEffect(() => {
    if (!open) setFaltou(false);
    else if (faltam.length > 0) setFaltou(true);
  }, [open, faltam.length]);

  const handleOpenChange = (aberto: boolean) => {
    // O bloqueio decide pelo dado de agora: o cache de 5 minutos manteria "faltam fotos" depois que o Parceiro enviou.
    if (aberto && exigeFotos && veiculoId) {
      void qc.invalidateQueries({ queryKey: chaveFotosVeiculo(veiculoId), refetchType: "none" });
    }
    setOpen(aberto);
  };

  const filteredTecnicos = useMemo(() => {
    return tecnicosDisponiveis.filter((tecnico) =>
      tecnico.nome.toLowerCase().includes(search.toLowerCase()),
    );
  }, [tecnicosDisponiveis, search]);

  const handleSelectTecnico = async (tecnico: UserProfile) => {
    setIsSelecting(true);
    setMessage(null);

    try {
      await onSelect(tecnico);
      setMessage({
        type: "success",
        text: `Técnico ${tecnico.nome} selecionado com sucesso!`,
      });
      setTimeout(() => {
        setOpen(false);
        setMessage(null);
      }, 2000);
    } catch (error) {
      setMessage({
        type: "error",
        // TypeError é a falha de rede do fetch (texto técnico em inglês); as demais trazem a mensagem do servidor, como o 409 das fotos.
        text:
          error instanceof Error && !(error instanceof TypeError) && error.message
            ? error.message
            : "Erro ao selecionar técnico. Tente novamente.",
      });

      setSelectedId(null);
      if (exigeFotos && veiculoId) {
        void qc.invalidateQueries({ queryKey: chaveFotosVeiculo(veiculoId) });
      }
    } finally {
      setIsSelecting(false);
    }
  };

  if (!laudoNaoVisitoriado) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" disabled={isLoading}>
          {tecnicoAtual ? "Trocar Técnico" : "Selecionar Técnico"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Selecionar Técnico de Vistoria
          </DialogTitle>
          <DialogDescription>
            Escolha um técnico disponível para realizar a vistoria
          </DialogDescription>
        </DialogHeader>

        {message && (
          <Alert
            variant={message.type === "success" ? "default" : "destructive"}
          >
            {message.type === "success" ? (
              <CheckCircle2 className="h-4 w-4" />
            ) : (
              <AlertCircle className="h-4 w-4" />
            )}
            <AlertDescription>{message.text}</AlertDescription>
          </Alert>
        )}

        {tecnicoAtual && (
          <Alert className="bg-blue-50 border-blue-200">
            <User className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              <strong>Técnico Atual:</strong> {tecnicoAtual.nome}
            </AlertDescription>
          </Alert>
        )}

        {mostrarFotos && (
          <div className="space-y-3">
            {!veiculoId ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Não foi possível identificar o veículo deste laudo.
                </AlertDescription>
              </Alert>
            ) : (
              <>
                {fotos.isPending ? (
                  <Skeleton className="h-14 rounded-lg" />
                ) : faltam.length > 0 ? (
                  <Alert>
                    <Camera className="h-4 w-4" />
                    <AlertDescription>
                      <p>{mensagemFotosFaltantes(faltam)}</p>
                      <p>Envie abaixo para liberar a escolha do técnico.</p>
                    </AlertDescription>
                  </Alert>
                ) : (
                  fotos.isSuccess && (
                    <Alert>
                      <CheckCircle2 className="h-4 w-4" />
                      <AlertDescription>
                        Fotos do veículo completas. Escolha o técnico.
                      </AlertDescription>
                    </Alert>
                  )
                )}
                <FotosVeiculo
                  veiculoId={veiculoId}
                  onEnviandoChange={setEnviandoFoto}
                />
              </>
            )}
            <Separator />
          </div>
        )}

        <div className="space-y-4">
          {/* Barra de Pesquisa */}
          <div className="relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Pesquisar por nome ou CREA..."
              className="pl-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={isSelecting || bloqueado}
            />
          </div>

          {/* Lista de Técnicos */}
          <div
            role="group"
            aria-label="Técnicos disponíveis"
            aria-disabled={bloqueado || undefined}
            className={cn(
              "space-y-2 max-h-96 overflow-y-auto",
              bloqueado && "opacity-50 pointer-events-none",
            )}
          >
            {isLoading ? (
              Array(3)
                .fill(0)
                .map((_, i) => <Skeleton key={i} className="h-20 rounded-lg" />)
            ) : filteredTecnicos.length > 0 ? (
              filteredTecnicos.map((tecnico) => (
                <Card
                  key={tecnico.id}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    selectedId === tecnico.id
                      ? "ring-2 ring-primary bg-primary/5"
                      : ""
                  }`}
                  onClick={() => {
                    if (bloqueado) return;
                    if (!isSelecting && selectedId != null) setSelectedId(null);
                    else setSelectedId(tecnico.id);
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-900">
                            {tecnico.nome}
                          </h3>
                          {selectedId === tecnico.id && (
                            <Badge className="bg-green-100 text-green-700">
                              Selecionado
                            </Badge>
                          )}
                        </div>
                        <div className="space-y-1">
                          {tecnico.email && (
                            <p className="text-sm text-slate-500">
                              {tecnico.email}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-2 mt-2">
                            {tecnico.crea && (
                              <Badge variant="outline" className="text-xs">
                                CREA: {tecnico.crea}
                              </Badge>
                            )}
                            {tecnico.telefone && (
                              <Badge variant="outline" className="text-xs">
                                {tecnico.telefone}
                              </Badge>
                            )}
                            {tecnico.cpf && (
                              <Badge variant="outline" className="text-xs">
                                CPF: {tecnico.cpf}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      {selectedId === tecnico.id && !isSelecting && (
                        <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <User className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Nenhum técnico encontrado</p>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              const tecnico = tecnicosDisponiveis.find(
                (t) => t.id === selectedId,
              );
              if (tecnico) {
                handleSelectTecnico(tecnico);
              }
            }}
            disabled={
              !selectedId ||
              isSelecting ||
              selectedId === tecnicoAtual?.id ||
              bloqueado ||
              enviandoFoto
            }
          >
            {isSelecting ? "Salvando..." : "Confirmar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
